/**
 * Report subscriptions (FINN 3 / F3) — pure model helpers shared by the
 * Subscribe control on Published Reports and the My subscriptions dashboard.
 * See .kiro/specs/report-subscriptions/design.md §6.
 */
import type { PublishedReport, ReportSubscription } from "../../models/API";
import { refreshModeOf } from "./publishedReportModel";

export const MY_SUBSCRIPTIONS_DASHBOARD_ID = "my-subscriptions";

/** The cadences a subscription can have (report_subscription.cadence). */
export type DigestCadence = "weekly" | "daily";
export const DIGEST_CADENCES: readonly DigestCadence[] = ["weekly", "daily"];

/** The hour digests go out, both cadences (the scheduler's default windows, design D6, D13). */
export const DIGEST_TIME_LABEL = "15:00 UTC (08:00 PDT / 07:00 PST)";
export const WEEKLY_SCHEDULE_LABEL = `Mondays at ${DIGEST_TIME_LABEL}`;
export const DAILY_SCHEDULE_LABEL = `every day at ${DIGEST_TIME_LABEL}`;

/** The URL parameter an email's unsubscribe link carries (Req 2.3). */
export const UNSUBSCRIBE_PARAM = "unsubscribe";

export const SNAPSHOT_TOOLTIP =
  "A snapshot never changes, so there is nothing to send. Only refreshable reports can be subscribed to.";

/** Only refreshable reports can be subscribed to (D1). */
export function canSubscribe(
  report: Pick<PublishedReport, "refresh_mode"> | null | undefined
): boolean {
  return !!report && refreshModeOf(report) === "rerun";
}

/** Active subscriptions keyed by report id. */
export function subscriptionByReport(
  rows: ReportSubscription[] | null | undefined
): Map<number, ReportSubscription> {
  const map = new Map<number, ReportSubscription>();
  (rows ?? []).forEach((row) => {
    if (row.active) map.set(row.report_id, row);
  });
  return map;
}

/** The subscription id in `?unsubscribe=<id>`, or null. */
export function readUnsubscribeParam(
  search: string = window.location.search
): number | null {
  const raw = new URLSearchParams(search).get(UNSUBSCRIBE_PARAM);
  const id = raw ? Number(raw) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Remove `unsubscribe=` from the address bar (no reload, no history entry). */
export function clearUnsubscribeParam(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(UNSUBSCRIBE_PARAM)) return;
  params.delete(UNSUBSCRIBE_PARAM);
  const query = params.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}`
  );
}

/** The subscription's report is still visible, published and refreshable. */
function reportStillSubscribable(row: ReportSubscription): boolean {
  return (
    row.report_title !== null &&
    row.report_title !== undefined &&
    row.report_active === true &&
    row.report_refresh_mode === "rerun" &&
    typeof row.report_team_id === "number"
  );
}

/** Whether an ended subscription can be taken up again (Req 2.2). */
export function canResubscribe(row: ReportSubscription): boolean {
  return !row.active && reportStillSubscribable(row);
}

/** Whether an active subscription's cadence can be switched (Req 1.3). */
export function canChangeCadence(row: ReportSubscription): boolean {
  return row.active && reportStillSubscribable(row);
}

/** "Report no longer available" when the report is unpublished or private to someone else (Req 2.5). */
export function subscriptionTitle(row: ReportSubscription): string {
  return row.report_title ?? "Report no longer available";
}

/** A row's cadence; anything but "daily" is the weekly default. */
export function cadenceOf(cadence: string | null | undefined): DigestCadence {
  return cadence === "daily" ? "daily" : "weekly";
}

export function cadenceLabel(cadence: string | null | undefined): string {
  return cadenceOf(cadence) === "daily" ? "Daily" : "Weekly";
}

/** When digests of `cadence` go out, e.g. "Mondays at 15:00 UTC (08:00 PDT / 07:00 PST)". */
export function scheduleLabel(cadence: string | null | undefined): string {
  return cadenceOf(cadence) === "daily"
    ? DAILY_SCHEDULE_LABEL
    : WEEKLY_SCHEDULE_LABEL;
}

/** "the daily digest, sent every day at 15:00 UTC (08:00 PDT / 07:00 PST)" */
export function digestDescription(cadence: string | null | undefined): string {
  return `the ${cadenceOf(cadence)} digest, sent ${scheduleLabel(cadence)}`;
}

/** Why an ended subscription ended. */
export function endedReasonLabel(reason: string | null | undefined): string {
  if (reason === "report_unpublished") return "Report unpublished";
  if (reason === "unsubscribed") return "Unsubscribed";
  return "Ended";
}

const DELIVERY_DETAIL_LABELS: Record<string, string> = {
  refreshed: "refreshed",
  reused: "reused a recent refresh",
  refresh_failed: "refresh failed, previous result sent",
  budget: "not refreshed (budget)",
  publisher_no_access: "not refreshed (publisher lost team access)",
  not_refreshable: "snapshot",
  no_access: "no access to the report",
  invalid_recipient: "no valid recipient",
  not_in_allowlist: "recipient not allowed on this stage",
};

/**
 * The last digest outcome for a subscription row, e.g.
 * "Sent 2026-W44 · refreshed", "Skipped 2026-W44 · no access to the report".
 * `formatTime` renders `last_delivered_at` (an RDS UTC timestamp) for display.
 */
export function deliveryLabel(
  row: Pick<
    ReportSubscription,
    | "last_period_key"
    | "last_delivery_status"
    | "last_delivery_detail"
    | "last_delivered_at"
  >,
  formatTime: (timestamp: string) => string = (t) => t
): string {
  const status = row.last_delivery_status;
  if (!status || !row.last_period_key) return "No digest yet";
  const detail = row.last_delivery_detail
    ? DELIVERY_DETAIL_LABELS[row.last_delivery_detail] ??
      row.last_delivery_detail
    : "";
  const when =
    status === "sent" && row.last_delivered_at
      ? formatTime(row.last_delivered_at)
      : row.last_period_key;
  const verb =
    status === "sent"
      ? "Sent"
      : status === "skipped"
      ? "Skipped"
      : status === "failed"
      ? "Failed"
      : "Unknown outcome";
  return detail ? `${verb} ${when} · ${detail}` : `${verb} ${when}`;
}
